const IST_TIMEZONE = 'Asia/Kolkata';

/** Calendar day in IST for appointment date APIs (`dd/MM/yyyy`). */
export function formatIstCalendarDateForApi(date: Date): string {
    return new Intl.DateTimeFormat('en-GB', {
        timeZone: IST_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(date);
}

/** `datetime-local` value (`YYYY-MM-DDTHH:mm`) in IST from a UTC ISO timestamp. */
export function toIstDatetimeLocalValue(iso: Date | string | null | undefined): string {
    if (!iso) return '';
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '';
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: IST_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(dt);
    const get = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((p) => p.type === type)?.value ?? '';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

/** Parse IST `datetime-local` wall time to UTC ISO for APIs. */
export function istDatetimeLocalToIso(local: string): string {
    const trimmed = local.trim();
    if (!trimmed) return '';
    return new Date(`${trimmed}:00+05:30`).toISOString();
}

const DATE_INPUT_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Normalize values for `<input type="date">` (YYYY-MM-DD, 4-digit year). */
export function normalizeDateInputValue(raw: string | null | undefined): string {
    if (raw == null) return '';
    const trimmed = String(raw).trim();
    if (!trimmed) return '';
    if (DATE_INPUT_ONLY.test(trimmed)) return trimmed;
    const fromIso = toIstDateInputValue(trimmed);
    if (fromIso && DATE_INPUT_ONLY.test(fromIso)) return fromIso;
    const head = trimmed.slice(0, 10);
    if (DATE_INPUT_ONLY.test(head)) return head;
    return trimmed;
}

/** Validate pharmacy batch expiry for stock-in (must be YYYY-MM-DD, year 2000–2100). */
export function validateDrugBatchExpiryInput(raw: string): string | null {
    const normalized = normalizeDateInputValue(raw);
    if (!normalized) return 'Expiry is required';
    const m = DATE_INPUT_ONLY.exec(normalized);
    if (!m) return 'Use YYYY-MM-DD (e.g. 2027-08-31)';
    const year = Number(m[1]);
    if (year < 2000 || year > 2100) {
        return `Invalid year (${year}). Use 4 digits between 2000 and 2100 — not ${normalized}`;
    }
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) return 'Invalid expiry date';
    const probe = new Date(Date.UTC(year, month - 1, day));
    if (
        probe.getUTCFullYear() !== year ||
        probe.getUTCMonth() !== month - 1 ||
        probe.getUTCDate() !== day
    ) {
        return 'Invalid expiry date';
    }
    return null;
}

/** `date` input value (`YYYY-MM-DD`) in IST from a UTC ISO timestamp. */
export function toIstDateInputValue(iso: Date | string | null | undefined): string {
    if (!iso) return '';
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '';
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: IST_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(dt);
}

/** Parse IST `date` input to UTC ISO (start of that calendar day in IST). */
export function istDateInputToIso(dateLocal: string): string {
    const trimmed = dateLocal.trim();
    if (!trimmed) return '';
    return new Date(`${trimmed}T00:00:00+05:30`).toISOString();
}

/** UTC ISO bounds for an IST calendar day (`YYYY-MM-DD`), for slot/appointment queries. */
export function istDayRangeIsoForApi(dateLocal: string): { startDate: string; endDate: string } {
    const trimmed = dateLocal.trim();
    return {
        startDate: new Date(`${trimmed}T00:00:00+05:30`).toISOString(),
        endDate: new Date(`${trimmed}T23:59:59.999+05:30`).toISOString(),
    };
}

/** Wall-clock time in IST as `HH:mm` (24h). */
export function formatIstTimeHHmm(iso: Date | string): string {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: IST_TIMEZONE,
    });
}

/** Combine IST date (`YYYY-MM-DD`) and wall time (`HH:mm`) → UTC ISO for APIs. */
export function istDateAndTimeToIso(dateLocal: string, timeHHmm: string): string {
    const date = dateLocal.trim();
    const [hours, minutes] = timeHHmm.split(':');
    const h = parseInt(hours, 10);
    const m = parseInt(minutes, 10);
    if (!date || Number.isNaN(h) || Number.isNaN(m)) return '';
    return new Date(
        `${date}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00+05:30`
    ).toISOString();
}

/** Generate IST wall-clock slot labels between shift start/end (exclusive end). */
export function generateIstSlotTimes(
    startHHmm: string,
    endHHmm: string,
    intervalMinutes = 15
): string[] {
    const [sh, sm] = startHHmm.split(':').map((n) => parseInt(n, 10));
    const [eh, em] = endHHmm.split(':').map((n) => parseInt(n, 10));
    if ([sh, sm, eh, em].some(Number.isNaN)) return [];

    let cursor = sh * 60 + sm;
    const end = eh * 60 + em;
    const slots: string[] = [];
    while (cursor < end) {
        const h = Math.floor(cursor / 60);
        const m = cursor % 60;
        slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
        cursor += intervalMinutes;
    }
    return slots;
}

/** Weekday name in IST for a calendar date string (`YYYY-MM-DD`). */
export function istWeekdayName(dateLocal: string): string {
    const trimmed = dateLocal.trim();
    if (!trimmed) return '';
    return new Date(`${trimmed}T12:00:00+05:30`)
        .toLocaleString('en-US', { weekday: 'long', timeZone: IST_TIMEZONE })
        .toUpperCase();
}

export const formatDate = (date: Date | string) =>
    new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        timeZone: IST_TIMEZONE,
    });

/** Date and time in IST (e.g. bill generation). */
export const formatDateTime = (date: Date | string) => {
    const dt = new Date(date);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: IST_TIMEZONE,
    });
};

export const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata'
    });
};

export const calculateAge = (dob: Date | string | null | undefined) => {
    if (dob == null || dob === undefined || dob === "") return;
    const dobDate = new Date(dob);
    if (Number.isNaN(dobDate.getTime())) return;
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const m = today.getMonth() - dobDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) age--;
    return age;
};

function coerceLeadAgeYears(age: number | string | null | undefined): number | null {
    if (age == null) return null;
    if (typeof age === 'number') {
        return Number.isFinite(age) && age >= 0 && age < 120 ? Math.round(age) : null;
    }
    const n = Number.parseInt(String(age).replace(/\D/g, ''), 10);
    return Number.isFinite(n) && n >= 0 && n < 120 ? n : null;
}

/** CRM lead → patient registration `date` input (`YYYY-MM-DD`). Uses DOB when set, else Jan 1 from age. */
export function leadPrefillDobForRegistration(options: {
    dob?: string | null;
    age?: number | string | null;
}): string {
    if (options.dob) {
        const fromDob = toIstDateInputValue(options.dob);
        if (fromDob) return fromDob;
    }
    const ageYears = coerceLeadAgeYears(options.age);
    if (ageYears != null) {
        const year = new Date().getFullYear() - ageYears;
        return `${year}-01-01`;
    }
    return '';
}
